import type { ReactNode } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import {
    DEFAULT_TABLE_ROW_COUNT,
    DesktopDateStackSkeleton,
    DesktopIconTitleCellSkeleton,
    DesktopIconTitleColumnSkeleton,
    DesktopPlaceholderDateStackSkeleton,
    DesktopStatusBadgeSkeleton,
    DesktopSubjectIconTwoLinesSkeleton,
    DesktopTransferActionSkeleton,
    DesktopTransferOwnershipSkeleton,
    mapSkeletonRows,
    MobileActionButtonSkeleton,
    MobileDateRowSkeleton,
    MobileEnvelopeListSkeleton,
    MobileCardTitleSkeleton,
    MobileEnvelopeRecipientSkeleton,
    MobileEnvelopeStatusSkeleton,
    MobileEnvelopeStyleRowSkeleton,
    MobileLabelBadgeRowSkeleton,
    MobileLabelRowSkeleton,
    MobileListCardSkeleton,
    MobileLogsActionBySkeleton,
    MobileLogsCategoryRowSkeleton,
    MobileLogsLineSkeleton,
    MobileStatusBadgeSkeleton,
    MobileTagListSkeleton,
    MobileTemplateStatsSkeleton,
    MobileTextDarkRowSkeleton,
    MobileTransferOwnershipSkeleton,
    MobileUserInfoSkeleton,
    MobileUserStatusRowSkeleton,
    TagMobileRowSkeleton,
    LogsMobileRowSkeleton,
    tableSkeletonLoaderId,
} from "@/components/skeletons/shared";

type RowCountProps = { rowCount?: number };
type ActionColumnProps = RowCountProps & { showActionColumn?: boolean };

const LOGS_ROW_COUNT = 15;
const INVITE_ROW_COUNT = 5;
const ADD_USER_ROW_COUNT = 6;

const ROLE_GROUP_MOBILE_ROWS = (
    <>
        <Skeleton className="gs-users-table-user-email gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--email gs-users-mobile-skeleton-line" />
        <MobileLabelRowSkeleton labelWidth="5.5rem" valueKind="count" />
        <MobileLabelRowSkeleton labelWidth="2.75rem" valueKind="count" />
        <MobileDateRowSkeleton labelWidth="4.875rem" />
    </>
);

const GROUP_MOBILE_ROWS = (
    <>
        <Skeleton className="gs-users-table-user-email gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--email gs-users-mobile-skeleton-line" />
        <MobileLabelRowSkeleton labelWidth="2.75rem" valueKind="count" />
        <MobileLabelRowSkeleton labelWidth="2.75rem" valueKind="count" />
        <MobileDateRowSkeleton labelWidth="4.875rem" />
    </>
);

const USERS_MOBILE_ROWS = (
    <>
        <Skeleton className="gs-users-table-user-email gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--email gs-users-mobile-skeleton-line" />
        <Skeleton className="gs-users-table-user-name gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--name gs-users-mobile-skeleton-line" />
        <MobileLabelRowSkeleton labelWidth="2.75rem" valueKind="count" />
        <MobileLabelRowSkeleton labelWidth="3.25rem" valueKind="count" />
        <MobileUserStatusRowSkeleton />
        <MobileDateRowSkeleton labelWidth="4.875rem" />
        <MobileDateRowSkeleton labelWidth="5.875rem" />
    </>
);

const ENVELOPE_MOBILE_ROWS = (
    <>
        <MobileCardTitleSkeleton variant="envelope" />
        <MobileEnvelopeRecipientSkeleton />
        <MobileTextDarkRowSkeleton labelWidth="3.5rem" valueWidth="5.5rem" />
        <MobileEnvelopeStatusSkeleton />
        <MobileDateRowSkeleton />
    </>
);

const REPORTED_ENVELOPE_MOBILE_ROWS = (
    <>
        <MobileCardTitleSkeleton variant="envelope" />
        <MobileEnvelopeRecipientSkeleton />
        <div className="gs-mobile-skeleton-text-line">
            <Skeleton className="gs-envelope-mobile-skeleton-reason gs-list-table-skeleton-placeholder" />
        </div>
        <MobileEnvelopeStatusSkeleton />
        <MobileDateRowSkeleton />
    </>
);

function mapEnvelopeStyleMobileRows(
    tableId: string,
    rowCount: number,
    renderFields: (index: number) => ReactNode
) {
    return (
        <MobileEnvelopeListSkeleton>
            {mapSkeletonRows(rowCount, (index) => (
                <MobileEnvelopeStyleRowSkeleton key={index} tableId={tableId} index={index}>
                    {renderFields(index)}
                </MobileEnvelopeStyleRowSkeleton>
            ))}
        </MobileEnvelopeListSkeleton>
    );
}

function mapMobileListCardRows(
    tableId: string,
    rowCount: number,
    rows: ReactNode,
    options?: { showChevron?: boolean; cardClassName?: string }
) {
    const showChevron = options?.showChevron ?? true;
    const cardClassName =
        options?.cardClassName ?? (showChevron ? "gs-mobile-list-card" : undefined);

    return (
        <>
            {mapSkeletonRows(rowCount, (index) => (
                <MobileListCardSkeleton
                    key={index}
                    tableId={tableId}
                    index={index}
                    showChevron={showChevron}
                    cardClassName={cardClassName}
                >
                    {rows}
                </MobileListCardSkeleton>
            ))}
        </>
    );
}

function IconCountDateTableSkeletonDesktop({
    rowCount = DEFAULT_TABLE_ROW_COUNT,
    tableId = "role",
}: RowCountProps & { tableId?: string }) {
    return (
        <>
            {mapSkeletonRows(rowCount, (index) => (
                <tr key={index} id={tableSkeletonLoaderId(tableId, index)} className="nk-tb-item gs-users-table-skeleton-row">
                    <td className="nk-tb-col"><DesktopIconTitleCellSkeleton /></td>
                    <td className="nk-tb-col">
                        <Skeleton className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--count" />
                    </td>
                    <td className="nk-tb-col">
                        <Skeleton className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--count" />
                    </td>
                    <td className="nk-tb-col">
                        <DesktopPlaceholderDateStackSkeleton />
                    </td>
                </tr>
            ))}
        </>
    );
}

// --- Envelope / reported envelope ---

function EnvelopeTableSkeletonDesktop({ rowCount = DEFAULT_TABLE_ROW_COUNT, showActionColumn = false }: ActionColumnProps) {
    return (
        <>
            {mapSkeletonRows(rowCount, (index) => (
                <tr key={index} id={tableSkeletonLoaderId("envelope", index)} className="nk-tb-item gs-users-table-skeleton-row">
                    {showActionColumn ? (
                        <td className="nk-tb-col gs-envelope-col-select align-middle">
                            <Skeleton className="gs-ownership-select-checkbox-skeleton" />
                        </td>
                    ) : null}
                    <td className="nk-tb-col gs-envelope-col-subject"><DesktopSubjectIconTwoLinesSkeleton /></td>
                    <td className="nk-tb-col gs-envelope-col-owner"><Skeleton className="h-4 w-[80px]" /></td>
                    <td className="nk-tb-col gs-envelope-col-status"><DesktopStatusBadgeSkeleton /></td>
                    <td className="nk-tb-col gs-envelope-col-created"><DesktopDateStackSkeleton /></td>
                    {showActionColumn ? (
                        <td className="nk-tb-col gs-envelope-col-actions"><DesktopTransferOwnershipSkeleton /></td>
                    ) : null}
                </tr>
            ))}
        </>
    );
}

function EnvelopeTableSkeletonMobile({
    rowCount = DEFAULT_TABLE_ROW_COUNT,
    showActionColumn = false,
}: ActionColumnProps) {
    return mapEnvelopeStyleMobileRows("envelope", rowCount, () => (
        <>
            {ENVELOPE_MOBILE_ROWS}
            {showActionColumn ? <MobileTransferOwnershipSkeleton /> : null}
        </>
    ));
}

function ReportedEnvelopeTableSkeletonDesktop({ rowCount = DEFAULT_TABLE_ROW_COUNT }: RowCountProps) {
    return (
        <>
            {mapSkeletonRows(rowCount, (index) => (
                <tr key={index} id={tableSkeletonLoaderId("reported-envelope", index)} className="nk-tb-item gs-users-table-skeleton-row">
                    <td className="nk-tb-col"><DesktopSubjectIconTwoLinesSkeleton titleWidth="180px" /></td>
                    <td className="nk-tb-col"><Skeleton className="h-4 w-full max-w-[180px]" /></td>
                    <td className="nk-tb-col"><DesktopStatusBadgeSkeleton /></td>
                    <td className="nk-tb-col"><DesktopDateStackSkeleton /></td>
                    <td className="nk-tb-col"><DesktopDateStackSkeleton /></td>
                </tr>
            ))}
        </>
    );
}

function ReportedEnvelopeTableSkeletonMobile({ rowCount = DEFAULT_TABLE_ROW_COUNT }: RowCountProps) {
    return mapEnvelopeStyleMobileRows("reported-envelope", rowCount, () => REPORTED_ENVELOPE_MOBILE_ROWS);
}

// --- Form / template / tag ---

function FormTableSkeletonDesktop({ rowCount = DEFAULT_TABLE_ROW_COUNT, showActionColumn = false }: ActionColumnProps) {
    return (
        <>
            {mapSkeletonRows(rowCount, (index) => (
                <tr key={index} id={tableSkeletonLoaderId("form", index)} className="nk-tb-item gs-users-table-skeleton-row">
                    {showActionColumn ? (
                        <td className="nk-tb-col gs-form-col-select align-middle">
                            <Skeleton className="gs-ownership-select-checkbox-skeleton" />
                        </td>
                    ) : null}
                    <td className="nk-tb-col gs-form-col-name subject-col">
                        <DesktopSubjectIconTwoLinesSkeleton
                            wrapperClassName="project-title"
                            iconClass="h-5 w-5 shrink-0 mr-2 mt-1"
                            titleWidth="160px"
                            subWidth="120px"
                        />
                    </td>
                    <td className="nk-tb-col gs-form-col-responses tb-col-mb"><Skeleton className="h-4 w-6" /></td>
                    <td className="nk-tb-col gs-form-col-status tb-col-mb"><DesktopStatusBadgeSkeleton width="60px" /></td>
                    <td className="nk-tb-col gs-form-col-owner tb-col-mb"><Skeleton className="h-4 w-[80px]" /></td>
                    {showActionColumn ? (
                        <td className="nk-tb-col gs-form-col-actions"><DesktopTransferOwnershipSkeleton /></td>
                    ) : null}
                </tr>
            ))}
        </>
    );
}

function FormTableSkeletonMobile({
    rowCount = DEFAULT_TABLE_ROW_COUNT,
    showActionColumn = false,
}: ActionColumnProps) {
    return mapEnvelopeStyleMobileRows("form", rowCount, () => (
        <>
            <MobileCardTitleSkeleton variant="form" />
            <MobileTextDarkRowSkeleton labelWidth="4.5rem" valueWidth="8rem" />
            <MobileLabelRowSkeleton labelWidth="4.75rem" valueKind="count" />
            <MobileLabelBadgeRowSkeleton labelWidth="3.5rem" badgeWidth="4.5rem" />
            <MobileLabelRowSkeleton labelWidth="3.5rem" valueWidth="6.5rem" />
            {showActionColumn ? <MobileTransferOwnershipSkeleton /> : null}
        </>
    ));
}

function TemplateTableSkeletonDesktop({ rowCount = DEFAULT_TABLE_ROW_COUNT, showActionColumn = false }: ActionColumnProps) {
    return (
        <>
            {mapSkeletonRows(rowCount, (index) => (
                <tr key={index} id={tableSkeletonLoaderId("template", index)} className="nk-tb-item gs-users-table-skeleton-row">
                    {showActionColumn ? (
                        <td className="nk-tb-col gs-template-col-select align-middle">
                            <Skeleton className="gs-ownership-select-checkbox-skeleton" />
                        </td>
                    ) : null}
                    <td className="nk-tb-col gs-template-col-name subject-col">
                        <DesktopIconTitleColumnSkeleton
                            iconClass="gs-list-table-skeleton-icon gs-list-table-skeleton-icon--template"
                            titleClass="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--template-name"
                        />
                    </td>
                    <td className="nk-tb-col gs-template-col-envelope-count tb-col-mb text-left">
                        <Skeleton className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--template-count" />
                    </td>
                    <td className="nk-tb-col gs-template-col-deleted-count tb-col-mb text-left">
                        <Skeleton className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--template-count" />
                    </td>
                    <td className="nk-tb-col gs-template-col-total-count tb-col-mb text-left">
                        <Skeleton className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--template-count" />
                    </td>
                    <td className="nk-tb-col gs-template-col-owner tb-col-mb text-left">
                        <Skeleton className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--template-owner" />
                    </td>
                    <td className="nk-tb-col gs-template-col-created tb-col-mb">
                        <DesktopPlaceholderDateStackSkeleton />
                    </td>
                    <td className="nk-tb-col gs-template-col-updated tb-col-mb">
                        <DesktopPlaceholderDateStackSkeleton />
                    </td>
                    {showActionColumn ? (
                        <td className="nk-tb-col gs-template-col-actions">
                            <DesktopTransferOwnershipSkeleton />
                        </td>
                    ) : null}
                </tr>
            ))}
        </>
    );
}

function TemplateTableSkeletonMobile({
    rowCount = DEFAULT_TABLE_ROW_COUNT,
    showActionColumn = false,
}: ActionColumnProps) {
    return mapEnvelopeStyleMobileRows("template", rowCount, () => (
        <>
            <MobileCardTitleSkeleton variant="template" />
            <MobileTextDarkRowSkeleton labelWidth="4.75rem" valueWidth="6.5rem" />
            <MobileTemplateStatsSkeleton />
            <MobileDateRowSkeleton />
            <MobileDateRowSkeleton labelWidth="5.125rem" />
            {showActionColumn ? <MobileTransferOwnershipSkeleton /> : null}
        </>
    ));
}

function TagTableSkeletonDesktop({ rowCount = DEFAULT_TABLE_ROW_COUNT }: RowCountProps) {
    return (
        <>
            {mapSkeletonRows(rowCount, (index) => (
                <tr key={index} id={tableSkeletonLoaderId("tag", index)} className="nk-tb-item gs-users-table-skeleton-row">
                    <td className="nk-tb-col gs-tag-col-name subject-col">
                        <DesktopIconTitleColumnSkeleton
                            iconClass="gs-list-table-skeleton-icon gs-list-table-skeleton-icon--tag"
                            titleClass="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--tag-name"
                            wrapTitleInInfo
                        />
                    </td>
                    <td className="nk-tb-col gs-tag-col-usage tb-col-mb">
                        <Skeleton className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--count" />
                    </td>
                    <td className="nk-tb-col gs-tag-col-created-by tb-col-mb">
                        <Skeleton className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--tag-creator" />
                    </td>
                    <td className="nk-tb-col gs-tag-col-created-at tb-col-mb">
                        <DesktopPlaceholderDateStackSkeleton />
                    </td>
                    <td className="nk-tb-col gs-tag-col-actions gs-tag-table-actions">
                        <Skeleton className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--tag-action" />
                    </td>
                </tr>
            ))}
        </>
    );
}

function TagTableSkeletonMobile({ rowCount = DEFAULT_TABLE_ROW_COUNT }: RowCountProps) {
    return (
        <MobileTagListSkeleton>
            {mapSkeletonRows(rowCount, (index) => (
                <TagMobileRowSkeleton key={index} tableId="tag" index={index} />
            ))}
        </MobileTagListSkeleton>
    );
}

// --- Role / group ---

function RoleTableSkeletonDesktop(props: RowCountProps) {
    return <IconCountDateTableSkeletonDesktop tableId="role" {...props} />;
}

function GroupTableSkeletonDesktop(props: RowCountProps) {
    return <IconCountDateTableSkeletonDesktop tableId="group" {...props} />;
}

function RoleTableSkeletonMobile(props: RowCountProps) {
    return mapMobileListCardRows("role", props.rowCount ?? DEFAULT_TABLE_ROW_COUNT, ROLE_GROUP_MOBILE_ROWS);
}

function GroupTableSkeletonMobile(props: RowCountProps) {
    return mapMobileListCardRows("group", props.rowCount ?? DEFAULT_TABLE_ROW_COUNT, GROUP_MOBILE_ROWS);
}

// --- Users ---

function UsersTableSkeletonDesktop({ rowCount = DEFAULT_TABLE_ROW_COUNT, showActionColumn = false }: ActionColumnProps) {
    return (
        <>
            {mapSkeletonRows(rowCount, (index) => (
                <tr key={index} id={tableSkeletonLoaderId("user", index)} className="gs-users-table-skeleton-row">
                    <td className="gs-users-td gs-ulist-col-user">
                        <div className="gs-users-table-user-cell">
                            <div className="gs-users-table-user-icon">
                                <Skeleton className="gs-list-table-skeleton-icon" aria-hidden />
                            </div>
                            <div className="gs-users-table-user-info">
                                <Skeleton className="gs-users-table-user-email gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--email" />
                                <Skeleton className="gs-users-table-user-name gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--name" />
                            </div>
                        </div>
                    </td>
                    <td className="gs-users-td gs-ulist-col-role">
                        <Skeleton className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--count" />
                    </td>
                    <td className="gs-users-td gs-ulist-col-group">
                        <Skeleton className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--count" />
                    </td>
                    <td className="gs-users-td gs-ulist-col-status">
                        <Skeleton className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--switch" />
                    </td>
                    <td className="gs-users-td gs-ulist-col-created">
                        <Skeleton className="gs-users-table-date gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--date" />
                        <Skeleton className="gs-users-table-date-sub gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--date-sub" />
                    </td>
                    <td className="gs-users-td gs-ulist-col-login">
                        <Skeleton className="gs-users-table-date gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--date" />
                        <Skeleton className="gs-users-table-date-sub gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--date-sub" />
                    </td>
                    {showActionColumn ? (
                        <td className="gs-users-td gs-ulist-col-action">
                            <DesktopTransferActionSkeleton />
                        </td>
                    ) : null}
                </tr>
            ))}
        </>
    );
}

function UsersTableSkeletonMobile({ rowCount = DEFAULT_TABLE_ROW_COUNT }: RowCountProps) {
    return mapMobileListCardRows("user", rowCount, USERS_MOBILE_ROWS);
}

// --- Logs, invite, reported users, add user ---

function LogsTableSkeletonDesktop({ rowCount = LOGS_ROW_COUNT }: RowCountProps) {
    return (
        <>
            {mapSkeletonRows(rowCount, (index) => (
                <tr key={index} id={tableSkeletonLoaderId("logs", index)} className="gs-users-table-skeleton-row">
                    <td className="gs-users-td gs-logs-col-created"><DesktopDateStackSkeleton /></td>
                    <td className="gs-users-td gs-logs-col-action"><Skeleton className="h-4 w-[60px]" /></td>
                    <td className="gs-users-td gs-logs-col-action-by">
                        <Skeleton className="h-4 w-[100px] max-w-full" />
                        <Skeleton className="gs-table-subject-skeleton-sub h-3 w-[120px] max-w-full" />
                    </td>
                    <td className="gs-users-td gs-logs-col-action-on">
                        <Skeleton className="h-4 w-[72px] max-w-full" />
                        <Skeleton className="gs-table-subject-skeleton-sub h-3 w-[120px] max-w-full" />
                    </td>
                    <td className="gs-users-td gs-logs-col-message">
                        <Skeleton className="h-4 w-full max-w-[240px]" />
                    </td>
                    <td className="gs-users-td gs-logs-col-category">
                        <DesktopStatusBadgeSkeleton width="72px" />
                    </td>
                </tr>
            ))}
        </>
    );
}

function LogsTableSkeletonMobile({ rowCount = LOGS_ROW_COUNT, wrap = true }: RowCountProps & { wrap?: boolean }) {
    const rows = mapSkeletonRows(rowCount, (index) => (
        <LogsMobileRowSkeleton key={index} tableId="logs" index={index}>
            <MobileLogsActionBySkeleton />
            <MobileLogsLineSkeleton labelWidth="4.5rem" valueWidth="12rem" valueMaxWidth="72%" spaced />
            <MobileLogsLineSkeleton labelWidth="3.25rem" valueWidth="7rem" valueMaxWidth="55%" spaced />
            <MobileLogsLineSkeleton labelWidth="4rem" valueWidth="14rem" valueMaxWidth="80%" spaced />
            <MobileLogsCategoryRowSkeleton />
            <MobileDateRowSkeleton labelWidth="4.875rem" />
        </LogsMobileRowSkeleton>
    ));

    if (!wrap) return <>{rows}</>;
    return <div className="envelope_table gs-logs-mobile">{rows}</div>;
}

function InviteUserTableSkeletonDesktop() {
    return (
        <>
            {mapSkeletonRows(INVITE_ROW_COUNT, (index) => (
                <tr key={index} id={tableSkeletonLoaderId("recent-invite", index)} className="gs-dashboard-table-skeleton-row">
                    <td className="table-td gs-dashboard-table-col-email">
                        <div className="gs-dashboard-table-email">
                            <div className="gs-dashboard-table-email-icon">
                                <Skeleton className="h-5 w-5 shrink-0 rounded-full" />
                            </div>
                            <Skeleton className="h-4 w-[220px] max-w-full" />
                        </div>
                    </td>
                    <td className="table-td gs-dashboard-table-col-status">
                        <DesktopStatusBadgeSkeleton width="7.5rem" />
                    </td>
                    <td className="table-td gs-dashboard-table-col-link">
                        <Skeleton className="h-4 w-[8.5rem]" />
                    </td>
                    <td className="table-td gs-dashboard-table-col-action">
                        <Skeleton className="mx-auto h-[calc(2.125rem+2px)] w-[5.75rem] rounded-[4px]" />
                    </td>
                </tr>
            ))}
        </>
    );
}

function InviteUserTableSkeletonMobile() {
    return mapMobileListCardRows(
        "recent-invite",
        INVITE_ROW_COUNT,
        (
            <div>
                <Skeleton className="gs-users-table-user-email gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--email" />
                <div className="meta mt-2">
                    <MobileStatusBadgeSkeleton width="7.5rem" />
                </div>
                <div className="mt-2">
                    <Skeleton className="gs-list-table-skeleton-placeholder h-4 w-[8.5rem] max-w-full" />
                </div>
                <div className="mt-2">
                    <MobileActionButtonSkeleton width="5.75rem" />
                </div>
            </div>
        ),
        { showChevron: false }
    );
}

function ReportedUsersTableSkeletonDesktop({ rowCount = DEFAULT_TABLE_ROW_COUNT }: RowCountProps) {
    return (
        <>
            {mapSkeletonRows(rowCount, (index) => (
                <tr key={index} id={tableSkeletonLoaderId("reported-users", index)} className="nk-tb-item gs-users-table-skeleton-row">
                    <td className="nk-tb-col gs-reported-users-col-user">
                        <DesktopSubjectIconTwoLinesSkeleton
                            iconClass="h-5 w-5 shrink-0 rounded-full mr-3"
                            titleWidth="160px"
                            subWidth="100px"
                        />
                    </td>
                    <td className="nk-tb-col gs-reported-users-col-count">
                        <Skeleton className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--count gs-reported-users-col-count-skeleton" />
                    </td>
                    <td className="nk-tb-col gs-reported-users-col-status">
                        <DesktopStatusBadgeSkeleton />
                    </td>
                    <td className="nk-tb-col gs-reported-users-col-actions">
                        <DesktopTransferActionSkeleton />
                    </td>
                </tr>
            ))}
        </>
    );
}

function ReportedUsersTableSkeletonMobile({ rowCount = DEFAULT_TABLE_ROW_COUNT }: RowCountProps) {
    return mapEnvelopeStyleMobileRows("reported-users", rowCount, () => (
        <>
            <MobileUserInfoSkeleton />
            <MobileLabelRowSkeleton labelWidth="11rem" valueKind="count" />
            <div className="text-dark">
                <Skeleton
                    className="gs-list-table-skeleton-placeholder shrink-0"
                    style={{ width: "3.25rem", height: "0.8125rem", minHeight: "0.8125rem" }}
                />
                <MobileStatusBadgeSkeleton width="50%" />
            </div>
            <div className="d-flex justify-content-between pt-1">
                <MobileActionButtonSkeleton width="5.5rem" />
            </div>
        </>
    ));
}

function AddUserTableSkeletonDesktop({ rowCount = ADD_USER_ROW_COUNT }: RowCountProps) {
    return (
        <>
            {mapSkeletonRows(rowCount, (index) => (
                <tr key={index} id={tableSkeletonLoaderId("add-user", index)} className="gs-users-table-skeleton-row">
                    <td className="w-10"><Skeleton className="h-4 w-4 rounded-sm" /></td>
                    <td>
                        <div className="flex items-center gap-2">
                            <Skeleton className="h-5 w-5 shrink-0 rounded-full" />
                            <Skeleton className="h-4 w-[140px] max-w-full" />
                        </div>
                    </td>
                    <td><Skeleton className="h-4 w-[180px] max-w-full" /></td>
                </tr>
            ))}
        </>
    );
}

function AddUserTableSkeletonMobile({ rowCount = ADD_USER_ROW_COUNT }: RowCountProps) {
    return (
        <MobileEnvelopeListSkeleton>
            {mapSkeletonRows(rowCount, (index) => (
                <div
                    className="envelope_tr gs-users-table-skeleton-mobile"
                    key={index}
                    id={tableSkeletonLoaderId("add-user", index, true)}
                >
                    <div className="meta my-1 shrink-0">
                        <Skeleton className="h-4 w-4 rounded-sm" aria-hidden />
                    </div>
                    <div className="env_meta_data gs-mobile-list-card__content min-w-0 flex-1">
                        <Skeleton className="gs-list-table-skeleton-placeholder h-4 w-[160px] max-w-full" />
                        <Skeleton className="gs-table-subject-skeleton-sub gs-list-table-skeleton-placeholder h-3 w-[200px] max-w-full" />
                        <Skeleton className="gs-table-subject-skeleton-sub gs-list-table-skeleton-placeholder h-3 w-[100px] max-w-full" />
                    </div>
                </div>
            ))}
        </MobileEnvelopeListSkeleton>
    );
}

export {
    AddUserTableSkeletonDesktop,
    AddUserTableSkeletonMobile,
    EnvelopeTableSkeletonDesktop,
    EnvelopeTableSkeletonMobile,
    FormTableSkeletonDesktop,
    FormTableSkeletonMobile,
    GroupTableSkeletonDesktop,
    GroupTableSkeletonMobile,
    InviteUserTableSkeletonDesktop,
    InviteUserTableSkeletonMobile,
    LogsTableSkeletonDesktop,
    LogsTableSkeletonMobile,
    ReportedEnvelopeTableSkeletonDesktop,
    ReportedEnvelopeTableSkeletonMobile,
    ReportedUsersTableSkeletonDesktop,
    ReportedUsersTableSkeletonMobile,
    RoleTableSkeletonDesktop,
    RoleTableSkeletonMobile,
    TagTableSkeletonDesktop,
    TagTableSkeletonMobile,
    TemplateTableSkeletonDesktop,
    TemplateTableSkeletonMobile,
    UsersTableSkeletonDesktop,
    UsersTableSkeletonMobile,
};
